import { ConverterService, CryptoProvider, SavePinService } from '../../../providers';
import { Injectable, Signal, inject } from '@angular/core';
import { UserService } from '../user.service';
import { UserServiceInterface } from '../interface';
import { VaultFacade } from '../../vault';

@Injectable({
  providedIn: 'root',
})
export class MobileUserServiceMigrationDecorator implements UserServiceInterface {
  private readonly converterService = inject(ConverterService);
  private readonly cryptoService = inject(CryptoProvider);
  private readonly userService = inject(UserService);
  private readonly vaultService = inject(VaultFacade);
  private readonly savePinService = inject(SavePinService);

  public hasSecondFactor: Signal<boolean | undefined> = this.userService.hasSecondFactor;
  public userSecret: Signal<string | undefined> = this.userService.userSecret;
  public username: Signal<string | undefined> = this.userService.username;

  public async getSecondFactor(): Promise<string | null> {
    await this.migrateOldSecondFactor();
    let pin = await this.userService.getSecondFactor();
    if (pin) {
      return pin;
    }

    const username = this.username();
    if (!username) {
      return null;
    }

    pin = await this.savePinService.getPin(username);

    if (pin) {
      await this.setSecondFactor(pin);
    }

    return pin;
  }

  public resetSecondFactor(): Promise<void> {
    return this.userService.resetSecondFactor();
  }

  public async setSecondFactor(secondFactor: string): Promise<void> {
    return this.userService.setSecondFactor(secondFactor);
  }

  private async decryptWithUserSecret(encryptedPin: string): Promise<string> {
    const userSecret = this.userSecret();
    if (!userSecret) {
      throw new Error('User secret is undefined');
    }
    const data = await this.cryptoService.decrypt(userSecret, encryptedPin, Uint8Array);

    return this.converterService.uIntArrayToString(data);
  }

  private async migrateOldSecondFactor(): Promise<void> {
    const oldSecondFactorKey = 'pin';
    const encryptedSecondFactor = await this.vaultService.getValue<string>(oldSecondFactorKey);
    if (encryptedSecondFactor !== null) {
      const decryptedSecondFactor = await this.decryptWithUserSecret(encryptedSecondFactor);
      await this.setSecondFactor(decryptedSecondFactor);
      await this.vaultService.removeValue(oldSecondFactorKey);
    }
  }
}
